import { Box, Divider, Flex, Select, Text, VStack } from '@chakra-ui/react';
import { formatPrice } from '@getjust/commons';
import { Button, JustSkeleton } from '@getjust/leaf';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';

import type { LoyaltyRedemptionOption } from '$business/atoms/loyalty';

import { loyaltyBalanceAtom, loyaltyDiscountCodeAtom } from '$business/atoms/loyalty';
import { useThemeContext } from '$src/context/ThemeContext';
import { useLoyaltyRedeem } from '$src/hooks/mutations/useLoyaltyRedeem';
import { useCart } from '$src/hooks/queries';
import { useLoyaltyBalance } from '$src/hooks/queries/useLoyaltyBalance';
import { useFormMessageAtom } from '$src/hooks/state';
import { useROEmailAtom } from '$src/hooks/state/useEmailAtom';

export const LoyaltyRewards = () => {
  const { t } = useTranslation();
  const email = useROEmailAtom();
  const { formMessage } = useFormMessageAtom();
  const phone = formMessage?.currentUser?.phone;
  const { data: cart } = useCart();
  const loyaltyBalance = useAtomValue(loyaltyBalanceAtom);
  const loyaltyDiscountCode = useAtomValue(loyaltyDiscountCodeAtom);
  const { isLoading: isLoadingBalance, isConfigured } = useLoyaltyBalance();
  const { redeem, isLoading: isRedeeming, isRedeemed: hasRedeemedOnce, isError } = useLoyaltyRedeem();

  const isLoyaltyDiscountInCart = useMemo(() => {
    if (!loyaltyDiscountCode || !cart?.discounts) return false;
    return cart.discounts.some((d) => d.code === loyaltyDiscountCode);
  }, [loyaltyDiscountCode, cart?.discounts]);

  const isRedeemed = hasRedeemedOnce && isLoyaltyDiscountInCart;
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>();

  const { themeName } = useThemeContext();
  const isCustom = themeName === 'custom';
  const isSoftOrClassic = themeName === 'soft' || themeName === 'classic';

  const availableRedemptions = useMemo(() => {
    if (!loyaltyBalance) return [];
    // Only offer rewards that resolve to an applicable cart discount — defensive against any
    // non-discount reward (free product / free shipping) that may still be in synced data.
    return loyaltyBalance.redemptionOptions.filter(
      (option) =>
        loyaltyBalance.points >= option.pointsCost &&
        !option.variantId &&
        (option.discountAmount != null || option.discountPercentage != null),
    );
  }, [loyaltyBalance]);

  const earningRatio = useMemo(() => {
    if (!loyaltyBalance?.campaigns?.length) return undefined;
    return loyaltyBalance.campaigns.find((c) => c.earningRatio)?.earningRatio;
  }, [loyaltyBalance]);

  const earnedPoints = useMemo(() => {
    if (!earningRatio || !cart?.totalAmount) return undefined;
    return Math.floor(earningRatio * cart.totalAmount);
  }, [earningRatio, cart?.totalAmount]);

  const handleRedeem = useCallback(async () => {
    if (!selectedOptionId || !loyaltyBalance) return;

    const option = loyaltyBalance.redemptionOptions.find((o) => String(o.id) === selectedOptionId);

    if (!option) return;

    await redeem({
      email: email || undefined,
      phone,
      platformCustomerId: formMessage?.currentUser?.id,
      redemptionOptionId: option.id,
      currency: loyaltyBalance.currency,
    });
  }, [email, phone, formMessage?.currentUser?.id, selectedOptionId, loyaltyBalance, redeem]);

  const formatRedemptionLabel = useCallback(
    (option: LoyaltyRedemptionOption) => {
      const currency = option.currency || loyaltyBalance?.currency || 'EUR';
      if (option.discountType === 'fixed_amount' && option.discountAmount != null) {
        return t('commons.loyalty_redeem_fixed', {
          amount: formatPrice(option.discountAmount, { currency }),
          points: option.pointsCost,
        });
      }
      if (option.discountPercentage != null) {
        return t('commons.loyalty_redeem_percentage', {
          percent: option.discountPercentage,
          points: option.pointsCost,
        });
      }
      return `${option.name} (${option.pointsCost} pts)`;
    },
    [t, loyaltyBalance?.currency],
  );

  const handleOptionChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedOptionId(value || undefined);
  }, []);

  if ((!email && !phone) || !isConfigured) {
    return null;
  }

  if (isLoadingBalance) {
    return <JustSkeleton height="20px" borderRadius="md" w="60%" />;
  }

  if (!loyaltyBalance) {
    return null;
  }

  const showPointEstimation = loyaltyBalance.showPointsEstimation !== false;
  const canSpendPoints = loyaltyBalance.allowPointsRedemption !== false;

  return (
    <Box w="100%">
      <Divider variant={isSoftOrClassic || isCustom ? 'solid' : 'dashed'} my="2" />

      <Flex justifyContent="space-between" alignItems="center" py="1">
        <Text fontSize="sm" fontWeight="600" lineHeight="6" color="var(--text, #1C1C1C)">
          {t('commons.loyalty_points')}
        </Text>
        <Text fontSize="sm" fontWeight="700" lineHeight="6" color="var(--text, #1C1C1C)">
          {loyaltyBalance.points.toLocaleString()} pts
        </Text>
      </Flex>

      {loyaltyBalance.tier && (
        <Text fontSize="xs" lineHeight="5" color="var(--text-muted, #8F8F8F)">
          {t('commons.loyalty_tier', { tier: loyaltyBalance.tier })}
        </Text>
      )}

      {showPointEstimation && earnedPoints !== undefined && earnedPoints > 0 && (
        <Text fontSize="xs" lineHeight="5" color="var(--text-secondary, #585858)" py="0.5">
          {t('commons.loyalty_order_earns')}{' '}
          <Text as="span" fontWeight="700" color="var(--text, #1C1C1C)">
            {t('commons.loyalty_order_earns_points', { points: earnedPoints.toLocaleString() })}
          </Text>
        </Text>
      )}

      {canSpendPoints && availableRedemptions.length > 0 && !isRedeemed && (
        <VStack gap={2} align="stretch" pt="2" pb="2">
          <Text fontSize="sm" fontWeight="500" lineHeight="6" color="var(--text, #1C1C1C)">
            {t('commons.loyalty_redeem_title')}
          </Text>
          <Flex gap={1.5} alignItems="center">
            <Select
              size="sm"
              data-ph-capture-attribute-tracking-id="loyalty-redeem-select"
              placeholder={t('commons.loyalty_redeem_placeholder') as string}
              onChange={handleOptionChange}
              value={selectedOptionId ?? ''}
              color="var(--text, #1C1C1C)"
              borderRadius="16px"
              borderColor="gray.602"
              h="10"
              flex="1 0 0"
              _focus={{ borderColor: 'gray.602', boxShadow: 'none' }}
              _focusVisible={{ borderColor: 'gray.602', boxShadow: 'none' }}
            >
              {availableRedemptions.map((option: LoyaltyRedemptionOption) => (
                <option key={String(option.id)} value={String(option.id)}>
                  {formatRedemptionLabel(option)}
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              trackingId="loyalty-redeem-button"
              isDisabled={!selectedOptionId || isRedeeming}
              isLoading={isRedeeming}
              onClick={handleRedeem}
              width="auto"
              h="10"
              label={t('commons.loyalty_redeem_button') as string}
              display="flex"
              py={2}
              px={4}
              justifyContent="center"
              alignItems="center"
              sx={{
                ...(isSoftOrClassic || isCustom
                  ? {
                      background: 'var(--button-active-bg)',
                      color: 'var(--button-active-text)',
                    }
                  : {}),
                '&:hover, &:disabled': {
                  bgImage: 'none',
                  border: '1px solid var(--border, #E0E0E0)',
                  background: 'var(--bg-secondary, #F7F7F7)',
                  color: 'var(--text, #1C1C1C)',
                },
                border: '1px solid transparent',
                fontWeight: 'medium',
                lineHeight: '6',
                fontSize: 'sm',
              }}
            />
          </Flex>
        </VStack>
      )}

      {canSpendPoints && isRedeemed && (
        <Text fontSize="sm" lineHeight="6" color="var(--text, #1C1C1C)" fontWeight="500" pt="1">
          {t('commons.loyalty_redeemed')}
        </Text>
      )}

      {canSpendPoints && isError && !isRedeemed && (
        <Text fontSize="sm" lineHeight="6" color="var(--error, #E53E3E)" fontWeight="500" pt="1">
          {t('commons.loyalty_redeem_error')}
        </Text>
      )}

      {canSpendPoints &&
        availableRedemptions.length === 0 &&
        !isRedeemed &&
        loyaltyBalance.redemptionOptions.length > 0 && (
          <Text fontSize="xs" lineHeight="5" color="var(--text-muted, #8F8F8F)">
            {t('commons.loyalty_not_enough_points')}
          </Text>
        )}

      <Divider variant={isSoftOrClassic || isCustom ? 'solid' : 'dashed'} my="2" />
    </Box>
  );
};
